import { PCCase, UserSubmittedCase } from '../types';


const STORAGE_KEY = 'user-submitted-cases';
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Generate a unique ID for a new case
 */
function generateCaseId(): string {
  return `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get all user-submitted cases from localStorage
 */
export function getUserCases(): UserSubmittedCase[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    return JSON.parse(stored) as UserSubmittedCase[];
  } catch (error) {
    console.error('Failed to load user cases:', error);
    return [];
  }
}

/**
 * Save a new user-submitted case via API, falling back to local storage
 */
export async function saveUserCase(
  caseData: Partial<PCCase>
): Promise<UserSubmittedCase> {
  const fallbackCase: UserSubmittedCase = {
    // Default values for optional fields
    id: generateCaseId(),
    weight: 0,
    fanMounts: [],
    driveBays: { internal35: 0, internal25: 0 },
    ioPanel: {},
    sidePanel: ['Steel'],
    colors: [],
    releaseYear: new Date().getFullYear(),
    imageUrl: '',
    thumbnailUrl: '',
    description: '',
    features: [],

    // Override with provided data
    ...caseData,

    // Ensure required fields have valid defaults
    name: caseData.name || 'Unknown Case',
    manufacturer: caseData.manufacturer || 'Unknown',
    sizeCategory: caseData.sizeCategory || 'Mid-Tower',
    dimensions: caseData.dimensions || { height: 0, width: 0, depth: 0 },
    maxGpuLength: caseData.maxGpuLength || 0,
    maxCpuCoolerHeight: caseData.maxCpuCoolerHeight || 0,
    maxPsuLength: caseData.maxPsuLength || 0,
    formFactorSupport: caseData.formFactorSupport || ['ATX'],

    isUserSubmitted: true,
    allSet: false,
  };

  // Try to save to API first — use server-returned ID
  try {
    const serverCase = await saveToApi(fallbackCase);
    // Save server version to localStorage with server ID
    const userCases = getUserCases();
    userCases.push(serverCase);
    saveAllUserCases(userCases);
    return serverCase;
  } catch (error) {
    console.warn('Failed to save case to API, saving locally:', error);
    // Fall back to local storage with client-generated ID
    const userCases = getUserCases();
    userCases.push(fallbackCase);
    saveAllUserCases(userCases);
    return fallbackCase;
  }
}

/**
 * Save case to API and return the server-created case with server ID
 */
async function saveToApi(caseData: UserSubmittedCase): Promise<UserSubmittedCase> {
  const response = await fetch(`${API_BASE_URL}/api/cases`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: caseData.name,
      manufacturer: caseData.manufacturer,
      dimensions: caseData.dimensions,
      weight: caseData.weight,
      sizeCategory: caseData.sizeCategory,
      maxGpuLength: caseData.maxGpuLength,
      maxCpuCoolerHeight: caseData.maxCpuCoolerHeight,
      maxPsuLength: caseData.maxPsuLength,
      sidePanel: caseData.sidePanel,
      colors: caseData.colors,
      releaseYear: caseData.releaseYear,
      imageUrl: caseData.imageUrl,
      thumbnailUrl: caseData.thumbnailUrl,
      productPageUrl: caseData.productPageUrl,
      description: caseData.description,
      features: caseData.features,
      formFactorSupport: caseData.formFactorSupport,
      fanMounts: caseData.fanMounts,
      driveBays: caseData.driveBays,
      ioPanel: caseData.ioPanel,
    }),
  });

  if (!response.ok) {
    throw new Error('API request failed');
  }

  const serverCase = await response.json();
  return { ...serverCase, isUserSubmitted: true } as UserSubmittedCase;
}

/**
 * Update an existing user-submitted case
 */
export function updateUserCase(
  caseId: string,
  updates: Partial<PCCase>
): UserSubmittedCase | null {
  const userCases = getUserCases();
  const index = userCases.findIndex((c) => c.id === caseId);

  if (index === -1) {
    return null;
  }

  userCases[index] = {
    ...userCases[index],
    ...updates,
  };

  saveAllUserCases(userCases);
  return userCases[index];
}

/**
 * Delete a user-submitted case
 */
export function deleteUserCase(caseId: string): boolean {
  const userCases = getUserCases();
  const filtered = userCases.filter((c) => c.id !== caseId);

  if (filtered.length === userCases.length) {
    return false; // Case not found
  }

  saveAllUserCases(filtered);
  return true;
}

/**
 * Get a specific user-submitted case by ID
 */
export function getUserCaseById(caseId: string): UserSubmittedCase | undefined {
  const userCases = getUserCases();
  return userCases.find((c) => c.id === caseId);
}

/**
 * Check if a case is a user-submitted case
 */
export function isUserSubmittedCase(caseId: string): boolean {
  return caseId.startsWith('user-');
}

/**
 * Save all user cases to localStorage
 */
function saveAllUserCases(cases: UserSubmittedCase[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cases));
  } catch (error) {
    console.error('Failed to save user cases:', error);
    // Handle localStorage quota exceeded
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      throw new Error(
        'Storage is full. Please delete some cases before adding new ones.'
      );
    }
    throw error;
  }
}

/**
 * Clear all user-submitted cases (for testing/reset)
 */
export function clearAllUserCases(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Export user cases as JSON (for backup)
 */
export function exportUserCases(): string {
  const userCases = getUserCases();
  return JSON.stringify(userCases, null, 2);
}

/**
 * Import user cases from JSON (for restore)
 */
export function importUserCases(json: string): number {
  try {
    const imported = JSON.parse(json) as UserSubmittedCase[];
    if (!Array.isArray(imported)) {
      throw new Error('Invalid format');
    }

    const existingCases = getUserCases();
    const existingIds = new Set(existingCases.map((c) => c.id));

    // Add only new cases (by ID)
    let addedCount = 0;
    for (const importedCase of imported) {
      if (!existingIds.has(importedCase.id)) {
        existingCases.push({
          ...importedCase,
          isUserSubmitted: true,
        });
        addedCount++;
      }
    }

    saveAllUserCases(existingCases);
    return addedCount;
  } catch (error) {
    throw new Error('Failed to import cases: invalid JSON format');
  }
}
